/* The member sidebar.
 *
 * The client's own list, in their order (docs/CLIENT_TERMINOLOGY.md):
 *
 *   Groups · Pages · Friends · News · Testimonies · Music bands · Singers
 *
 * There is no separate "Administration" entry. Groups goes straight to the
 * church's groups, and the API decides what a given person may do there — a
 * menu item that only some people can use is a worse explanation of
 * permissions than the screen itself.
 *
 * GROUPED, not one flat run of seven. Facebook's rail separates what you
 * belong to from what you browse, with a heading and a rule between — seven
 * undifferentiated rows is a list to read rather than a menu to use. The
 * client's order is preserved inside the groups.
 *
 * Items whose screens are not built yet render but are inert. They are in the
 * client's design, so leaving them out would misrepresent the product; the
 * tooltip says why nothing happens rather than a badge on every row.
 */
import { NavLink } from 'react-router-dom'
import {
  CalendarCheck,
  Church,
  ClipboardCheck,
  Contact,
  FileText,
  ListChecks,
  Mic2,
  Music,
  Newspaper,
  Phone,
  Send,
  Shield,
  Star,
  Users,
} from 'lucide-react'
import { useFeatures, useMe, useMyPermissions } from '@/api/queries'
import { cn } from '@/lib/utils'

type Item = {
  label: string
  icon: typeof Users
  to?: string          // present once the screen exists
  /** Hidden from ordinary members. Only Permissions, which is an admin tool
   *  and would 403 for anyone else. */
  adminOnly?: boolean
  /** Shown to whoever keeps attendance — ushers as well as administrators. */
  attendanceOnly?: boolean
  /** Needs a church to mean anything. */
  churchOnly?: boolean
  /** Whoever may change what joining asks — members.questions. */
  questionsOnly?: boolean
  /** Hidden when the church has none of these switched on. */
  needs?: 'messaging' | 'meetings'
}

type Group = {
  heading?: string
  items: Item[]
}

/* The client's seven, in their order, split where the meaning changes:
   things you are part of, then things you browse. */
const GROUPS: Group[] = [
  {
    items: [
      // The wired-up ones carry a `to`. The rest are the client's menu, shown
      // so the shape of the product reads correctly, and inert.
      { label: 'Groups', icon: Users, to: '/groups' },
      // Every member's: checking in, saying you will be away, and how often
      // you have been. Whoever manages attendance also sets up the services
      // and the check-in location here.
      { label: 'Services', icon: CalendarCheck, to: '/services', churchOnly: true },
      { label: 'Attendance', icon: ClipboardCheck, to: '/attendance', attendanceOnly: true },
      // Under Groups, because it answers the question groups raise: who is
      // in this church, and how many groups are they in?
      //
      // ADMIN ONLY. The church member list endpoint is admin-only, so a
      // member following this link reached a page that could only ever say
      // "No members yet" — an empty screen where the API had actually
      // refused them.
      { label: 'Members', icon: Contact, to: '/members', adminOnly: true },
      { label: 'Permissions', icon: Shield, to: '/permissions', adminOnly: true },
      // What everybody joining is asked. The church's own to change.
      { label: 'Join questions', icon: ListChecks, to: '/join-questions', questionsOnly: true },
      // Writing to the whole church. Beside Members and Permissions because
      // it is the same kind of authority, and hidden from everybody else for
      // the same reason: the page would only ever refuse them.
      { label: 'Messages', icon: Send, to: '/messages', adminOnly: true, needs: 'messaging' },
      // Calls sit beside Messages because they answer the same question —
      // who is this for — and are set up by the same people.
      { label: 'Meetings', icon: Phone, to: '/calls', adminOnly: true, needs: 'meetings' },
      { label: 'Pages', icon: FileText },
      { label: 'Friends', icon: Contact },
    ],
  },
  {
    heading: 'Explore',
    items: [
      { label: 'News', icon: Newspaper },
      { label: 'Testimonies', icon: Star, to: '/testimonies' },
      { label: 'Music bands', icon: Music },
      { label: 'Singers', icon: Mic2 },
    ],
  },
]

export function LeftSidebar() {
  const { data: me } = useMe()
  /* A ROLE IS NOT ONLY church_admin. This gated purely on the account role, so
     somebody given a church Moderator role with members.view saw the badge
     change and nothing else — the pages stayed hidden even though the server
     now allows them.

     members.view is the right question for both links: the Members page IS
     that list, and Permissions is where roles are assigned, which nobody
     without at least member visibility has any use for. The server checks
     again on every call, so this decides what is worth showing, not what is
     allowed. */
  const permissions = useMyPermissions(me?.church_id ?? null)
  const isAdmin =
    me?.role === 'church_admin' ||
    me?.role === 'super_admin' ||
    (permissions.data?.includes('members.view') ?? false)
  const keepsAttendance =
    me?.role === 'church_admin' ||
    me?.role === 'super_admin' ||
    (permissions.data ?? []).some(
      (p) => p === 'attendance.mark' || p === 'attendance.manage',
    )
  const inChurch = Boolean(me?.church_id)
  const managesQuestions =
    me?.role === 'church_admin' ||
    me?.role === 'super_admin' ||
    (permissions.data?.includes('members.questions') ?? false)
  const features = useFeatures()
  const offered = {
    messaging: features.anyMessaging,
    meetings: features.anyMeetings,
  }

  return (
    <aside className="sticky top-[72px] pr-s2 max-h-[calc(100vh-88px)] overflow-y-auto">
      {/* WHICH CHURCH YOU ARE IN. The rail is on every page, so this is the
          only place the answer is always visible — the name was previously
          only on the Groups page and in the account menu, which meant the feed
          gave no church context at all.

          With no church it becomes the way IN. "Join one" used to be the
          advice on the Groups empty state with nothing to click behind it. */}
      {me ? (
        me.church_name ? (
          <div className="mb-s2 flex items-center gap-s3 rounded-md px-s2 py-s2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
              <Church className="h-4 w-4" aria-hidden />
            </span>
            {/* The name alone. "Your church" underneath said nothing the
                name did not, and cost the row the space the name needed —
                "Grace Community Chur…" was truncating with a label under it
                explaining what it was. */}
            <span className="min-w-0 truncate text-md font-semibold">
              {me.church_name}
            </span>
          </div>
        ) : (
          <NavLink
            to="/find-church"
            className="mb-s2 flex items-center gap-s3 rounded-md px-s2 py-s2 transition-colors hover:bg-hover"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
              <Church className="h-4 w-4" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-md font-semibold text-brand">
                Find a church
              </span>
              <span className="block text-sm text-ink-2">You have not joined one</span>
            </span>
          </NavLink>
        )
      ) : null}

      {GROUPS.map((group, i) => (
        <div key={group.heading ?? i}>
          {i > 0 ? <Divider /> : null}
          {group.heading ? (
            <h2 className="px-s2 pb-s1 pt-s2 text-md font-semibold text-ink-2">
              {group.heading}
            </h2>
          ) : null}
          <nav>
            <ul>
              {group.items
                .filter((item) => !item.adminOnly || isAdmin)
                .filter((item) => !item.attendanceOnly || (inChurch && keepsAttendance))
                .filter((item) => !item.questionsOnly || (inChurch && managesQuestions))
                .filter((item) => !item.churchOnly || inChurch)
                .filter((item) => !item.needs || offered[item.needs])
                .map(({ label, icon: Icon, to }) => (
                <li key={label}>
                  {to ? (
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-s3 rounded-md px-s2 py-s2 text-md font-semibold transition-colors',
                          isActive
                            ? 'bg-brand-soft text-brand'
                            : 'text-ink hover:bg-hover-2',
                        )
                      }
                    >
                      <Icon className="h-6 w-6 shrink-0 text-ink-2" aria-hidden />
                      {label}
                    </NavLink>
                  ) : (
                    /* Readable, just quieter than a live link. Fading these to
                       near-invisible made the client's own menu unreadable —
                       "not yet" is not the same as "not worth reading". */
                    <span
                      className="flex cursor-default items-center gap-s3 rounded-md px-s2 py-s2 text-md font-semibold text-ink-2"
                      title={`${label} is not available yet`}
                    >
                      <Icon className="h-6 w-6 shrink-0" aria-hidden />
                      {label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      ))}
    </aside>
  )
}

/** Inset from the row padding, so it separates the groups without cutting the
 *  full width of the column. */
function Divider() {
  return <div className="mx-s2 my-s2 h-px bg-line-2" />
}
